import * as _ from 'lodash';
import enums from '../enum';

const specMatches = (spec, testValue) => {
	if (!spec) { return false; }

	if (_.isArray(testValue)) {
		return _.indexOf(testValue, spec.value) !== -1;
	}
	else {
		const regex = new RegExp(testValue);
		return regex.test(spec.value);
	}
};

const passesFilters = (specs, requires, excludes) => {
	let method = (requires && _.isArray(requires)) ? 'some' : 'every';
	const meetsRequirements = !requires || _[method](requires, (val, key) => {
		if (_.isPlainObject(val)) {
			return _.every(val, (innerVal, innerKey) => {
				const spec = _.find(specs, { specKey: innerKey });
				return specMatches(spec, innerVal);
			});
		}
		else {
			const spec = _.find(specs, { specKey: key });
			return specMatches(spec, val);
		}
	});

	if (meetsRequirements) {
		method = (excludes && _.isArray(excludes)) ? 'some' : 'every';
		return !excludes || _[method](excludes, (val, key) => {
			if (_.isPlainObject(val)) {
				return _.every(val, (innerVal, innerKey) => {
					const spec = _.find(specs, { specKey: innerKey });
					return !specMatches(spec, innerVal);
				});
			}
			else {
				const spec = _.find(specs, { specKey: key });
				return !specMatches(spec, val);
			}
		});
	}

	return false;
};

class Specs {
	_jobCost;
	_def;
	_settingName;
	_specGroup;
	_locked;
	_specs;
	_items;

	constructor(jobCost, settingName, specGroup, locked = false) {
		this._jobCost = jobCost;
		this._def = {};
		this._settingName = settingName;
		this._specGroup = specGroup;
		this._locked = locked;
		this._specs = {};
		this._items = {};
	}
	getSpecDefinitionSetting(workAreaKey) {
		return this._jobCost.getSetting(enums.jobCostSettingGroups.General, this._settingName, workAreaKey, true);
	}
	canSet(workAreaKey) {
		return !!this._def[workAreaKey];
	}
	getSpecifications(workAreaKey) {
		if (workAreaKey && !this._specs[workAreaKey]) {
			this._specs[workAreaKey] = (this._jobCost.specifications) ? _.filter(this._jobCost.specifications, { group: this._specGroup, workAreaKey: workAreaKey }) : [];
		}

		return this._specs[workAreaKey];
	}
	items(workAreaKey) {
		if (workAreaKey && !this._items[workAreaKey]) {
			const specs = this.getSpecifications(workAreaKey);

			if (!this._def[workAreaKey]) {
				return _.map(specs, v => {
					return {
						key: `${workAreaKey}-${v.specKey}`,
						label: v.name,
						value: v.value,
						values: [],
						hidden: false,
						disabled: true,
						allowImage: false,
						setValue: val => {
							this.setValue(v.specKey, val, workAreaKey);
						}
					};
				});
			}

			this._items[workAreaKey] = _.map(this._def[workAreaKey], (defVal, defKey) => {
				const resultItem = {
					key: `${workAreaKey}-${defKey}`,
					label: defVal.label,
					values: [],
					hasValues: !!(defVal.values && defVal.values.length),
					preventCustomValue: !!defVal.preventCustomValue,
					hidden: false,
					disabled: false,
					unavailableMode: defVal.unavailableMode,
					model: null,
					allowImage: !!defVal.allowImage,
					setValue: val => {
						this.setValue(defKey, val, workAreaKey);
					}
				};
				const spec = _.find(specs, { specKey: defKey });
				// supports change orders
				resultItem.model = spec;

				if (!passesFilters(specs, defVal.requires, defVal.excludes)) {
					if (resultItem.unavailableMode === 'hidden') {
						resultItem.hidden = true;
					}
					else {
						resultItem.disabled = true;
					}
				}

				if (!resultItem.disabled && !resultItem.hidden && defVal.values && defVal.values.length) {
					// fill'er up with values
					const vals = defVal.values;

					if (typeof vals[0] === 'string') { // peak at the first resultItem to see if these are just strings
						resultItem.values = vals;
					}
					else {
						_.forEach(vals, vVal => {
							if (passesFilters(specs, vVal.requires, vVal.excludes)) {
								resultItem.values = resultItem.values.concat(vVal.items);
								if (vVal.autoSet) {
									this.setValue(defKey, vVal.items, workAreaKey);
									(resultItem as any).value = vVal.items;
									(resultItem as any).autoSet = true;
								}
							}
						});
					}

					resultItem.values = resultItem.values.sort();
				}

				if (spec && (resultItem.disabled || resultItem.hidden)) {
					// clear this spec because it must not be valid any more
					spec.value = undefined;
				}

				if (spec) {
					(resultItem as any).value = spec.value;
				}

				return resultItem;
			}) || [];
		}

		return this._items[workAreaKey] || [];
	}
	setValue(key, value, workAreaKey) {
		if (!this.canSet(workAreaKey)) { throw new Error('Setting of specs is currently not allowed.'); }
		if (this._locked) { return; }

		let spec = _.find(this.getSpecifications(workAreaKey), { specKey: key });
		const def = this._def[workAreaKey];

		if (value) {
			// All changes in a change order require a new spec, if the value is equal to the parent, setting the value below will remove it
			if (!spec) {
				spec = this._jobCost.specifications.add({ group: this._specGroup, specKey: key, workAreaKey: workAreaKey, name: def[key].label });
				this._invalidateSpecs(workAreaKey);
			}
		}
		else if (spec) {
			this._jobCost.specifications.delete(spec.key);
			spec = undefined;
			this._invalidateSpecs(workAreaKey);
		}

		if (spec && spec.value !== value) {
			spec.value = value;
			this._invalidateSpecs(workAreaKey);
		}
	}
	setDefinition(workAreaKey, config, name) {
		if (!this._def[workAreaKey] || this._def[workAreaKey] !== config) {
			this._def[workAreaKey] = config;
			this._invalidateSpecs(workAreaKey);
		}

		if (this._locked) { return; } //enabling this will lock CO version to the base version afaik. 

		if (name) {
			const definitionSetting = this.getSpecDefinitionSetting(workAreaKey);
			if (name !== definitionSetting.value) {
				definitionSetting.value = name;
				this._invalidateSpecs(workAreaKey);
			}
		}
	}

	clear(workAreaKey) {
		this._def[workAreaKey] = undefined;
		const definitionSetting = this.getSpecDefinitionSetting(workAreaKey);

		if (definitionSetting) {
			this._jobCost.settings.delete(definitionSetting.key);
		}

		if (this._jobCost.specifications) {
			const jobCost = this._jobCost;

			_.forEach(_.filter(this._jobCost.specifications, { group: this._specGroup, workAreaKey: workAreaKey }), s => {
				jobCost.specifications.delete(s.key);
			});

			this._invalidateSpecs(workAreaKey);
		}
	}

	_invalidateSpecs(workAreaKey) {
		// console.log('invalidating specs ' + workAreaKey);
		if (workAreaKey) {
			this._specs[workAreaKey] = undefined;
			this._items[workAreaKey] = undefined;
		}
		else {
			this._specs = {};
			this._items = {};
		}
	}

	clone(sourceWorkAreaKey, destinationWorkAreaKey) {
		this.clear(destinationWorkAreaKey);

		const definitionSetting = this.getSpecDefinitionSetting(sourceWorkAreaKey);

		this.setDefinition(destinationWorkAreaKey, this._def[sourceWorkAreaKey], definitionSetting.value);

		if (this._jobCost.specifications) {
			_.forEach(_.filter(this._jobCost.specifications, { group: this._specGroup, workAreaKey: sourceWorkAreaKey }), s => {
				this.setValue(s.specKey, s.value, destinationWorkAreaKey);
			});

			this._invalidateSpecs(destinationWorkAreaKey);
		}
	}
}

export default Specs;